import React from 'react';
import { Group } from 'apps/manage/types';
import { useTheme, ListItemIcon, ListItem, ListItemText } from '@mui/material';
import AvatarGroup from '@mui/material/AvatarGroup';
import Icon from 'components/icon2';
import p from 'translation';
import Avatar from 'core/components/avatar';

interface Props {
  group: Group;
  level: number;
  noAccess?: boolean;
  onClick: (group: Group) => void;
}

function getSpacing(level: number): number {
  switch (level) {
    case 0:
      return 1;
    case 1:
      return 2;
    case 2:
      return 6;
    case 3:
      return 10;
    case 4:
      return 14;
    default:
      return level * 4;
  }
}

interface GroupManagersProps {
  group: Pick<Group, 'users'>;
}

function GroupManagers({ group }: GroupManagersProps): JSX.Element | null {
  if (!group.users) return null;

  const managers = group.users.filter(user =>
    user.roles.find(role => role.id === 'group:manager'),
  );
  if (managers.length === 0) {
    return null;
  }

  return (
    <ListItemIcon style={{ marginRight: '1em', justifyContent: 'flex-end' }}>
      <AvatarGroup>
        {managers.map(user => (
          <Avatar
            key={user.user.id}
            user={user.user}
            className='manager-profile-picture'
          />
        ))}
      </AvatarGroup>
    </ListItemIcon>
  );
}

export default function GroupRow({
  group,
  noAccess,
  level,
  onClick,
}: Props): JSX.Element {
  const theme = useTheme();

  return (
    <ListItem
      button
      divider
      style={{
        paddingLeft: theme.spacing(getSpacing(level)),
      }}
      disabled={noAccess}
      onClick={() => onClick(group)}
    >
      {level > 0 && (
        <Icon
          icon='level-up'
          rotation={90}
          style={{ marginRight: theme.spacing(2) }}
        />
      )}
      <ListItemText
        primary={group.name || p.t('common.pagination.see-more')}
        secondary={
          group.frames &&
          group.users &&
          `${p.t('apps.manage.groups.in-group.frames', group.frames.length)}, ${p.t('apps.manage.groups.in-group.users', group.users.length)}`
        }
      />
      <GroupManagers group={group} />
      <Icon icon='chevron-right' />
    </ListItem>
  );
}
